// src/utils/abrirApp.js
//
// Ponto ÚNICO de "levar a pessoa para o app".
//
// Regra do produto: em QUALQUER lugar (landing, funil, modal de CPF já
// cadastrado, pós-pagamento) o botão não pergunta nada nem mostra escolha de
// loja — detecta o sistema do aparelho e resolve:
//
//     app instalado  → abre o app (universal link / scheme)
//     não instalado  → App Store (iOS) ou Play Store (Android)
//     desktop        → página /baixar, que mostra as duas lojas
//
// COMO A DETECÇÃO DE "INSTALADO" FUNCIONA: não existe API que responda isso no
// navegador, e o truque clássico (abrir o link e ver se a página perdeu o foco
// num timer) NÃO serve aqui — quando o app não está instalado o navegador
// segue a URL, a página descarrega e o timer morre junto. Por isso a decisão é
// delegada a quem sabe respondê-la: no Android, ao próprio Chrome, via
// `intent://` com `browser_fallback_url`; no iOS, à App Store, que mostra
// "ABRIR" para quem já tem o app.

const APP_STORE_URL = "https://apps.apple.com/br/app/lesvita/id6753772111";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.lesvita.app";

const ua = () =>
  (typeof navigator !== "undefined" ? navigator.userAgent || "" : "");

export const isIOS = () => /iPhone|iPad|iPod/i.test(ua());
export const isAndroid = () => /Android/i.test(ua());
export const isMobile = () => isIOS() || isAndroid();

/** Loja do aparelho atual (null no desktop). */
export function lojaDoAparelho() {
  if (isIOS()) return APP_STORE_URL;
  if (isAndroid()) return PLAY_STORE_URL;
  return null;
}

const PACOTE_ANDROID = "com.lesvita.app";

/**
 * Abre o app; se não estiver instalado, cai na LOJA do próprio sistema.
 *
 * A versão anterior navegava a página para o universal link
 * (https://www.lesvita.com/app/<destino>) e agendava um timer de 1,4s para
 * cair na loja. Isso não funcionava quando o app NÃO estava instalado — que é
 * justamente o caso do botão "Acessar app" logo depois de pagar na web:
 * o navegador seguia a URL, a página descarregava, o timer morria junto e a
 * pessoa aterrissava no site (rota /app/* → /login), nunca na loja.
 *
 * Agora cada sistema usa o mecanismo que ele resolve sozinho, sem timer e sem
 * corrida:
 *
 *   Android — URL `intent://` com `S.browser_fallback_url`. O próprio Chrome
 *             abre o app se estiver instalado e vai para a Play Store se não
 *             estiver. Uma decisão só, tomada pelo sistema.
 *
 *   iOS     — App Store direto. O universal link só levaria ao app para quem
 *             JÁ o tem, e levaria ao SITE para quem não tem — o bug. A App
 *             Store mostra "ABRIR" para quem já tem o app, então o caso
 *             instalado continua a um toque, e o caso não-instalado (a maioria
 *             de quem acabou de assinar) passa a estar certo.
 *
 * @param {object} opts
 * @param {string} opts.destino  rota interna do app (malha /app/*), ex.: "login".
 * @param {string} opts.origem   marcação de origem para telemetria do app.
 */
export function abrirApp({ destino = "vita", origem = "web" } = {}) {
  // Desktop: não há app para abrir — /baixar mostra as duas lojas.
  if (!isMobile()) {
    window.location.assign("/baixar");
    return;
  }

  if (isAndroid()) {
    const caminho =
      `www.lesvita.com/app/${destino}?origem=${encodeURIComponent(origem)}`;
    window.location.assign(
      `intent://${caminho}#Intent;scheme=https;package=${PACOTE_ANDROID};` +
      `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`
    );
    return;
  }

  window.location.assign(APP_STORE_URL);
}

export const APP_STORE = APP_STORE_URL;
export const PLAY_STORE = PLAY_STORE_URL;
